import React from 'react';
import { useAuth } from '../context/AuthContext';
import '../pages/JewelleryListPage.css'; // Ensure styles are available

const ProductCard = ({ item, isAdmin, onEdit, onDelete, onToggleVisibility, onView, isSelected, onSelect, index = 99 }) => {
    const { user } = useAuth();

    // Helpers
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
        const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';
        const baseUrl = isProduction ? '' : 'http://localhost:5050';
        const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
        return `${baseUrl}${cleanPath.replace(/\\/g, '/')}`;
    };

    // Status Badge Logic
    const isDraft = item.status === 'Draft';
    const isHidden = !item.visibility;
    const isSold = item.status === 'Sold';

    // "New" Badge Logic (7 days)
    const isNew = (() => {
        if (!item.createdAt) {
            // Fallback to ObjectId timestamp
            const timestamp = parseInt(item._id.substring(0, 8), 16) * 1000;
            return (new Date() - new Date(timestamp)) < (7 * 24 * 60 * 60 * 1000);
        }
        return (new Date() - new Date(item.createdAt)) < (7 * 24 * 60 * 60 * 1000);
    })();

    const getTypeIcon = (type) => {
        const lowerType = type?.toLowerCase() || '';

        if (lowerType.includes('gold')) {
            return (
                <svg className="type-icon" viewBox="0 0 24 24" fill="currentColor">
                    <title>Gold</title>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
            );
        } else if (lowerType.includes('silver')) {
            return (
                <svg className="type-icon" viewBox="0 0 24 24" fill="currentColor">
                    <title>Silver</title>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                </svg>
            );
        } else if (lowerType.includes('ring')) {
            return (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <title>Ring</title>
                    <circle cx="12" cy="12" r="7" />
                    <path d="M12 5l-2-2h4l-2 2z" fill="currentColor" stroke="none" />
                </svg>
            );
        } else if (lowerType.includes('necklace') || lowerType.includes('chain') || lowerType.includes('mangal')) {
            return (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <title>Necklace</title>
                    <path d="M12 21c5-3 8-8 8-13a8 8 0 1 0-16 0c0 5 3 10 8 13z" />
                    <circle cx="12" cy="16" r="2" fill="currentColor" />
                </svg>
            );
        } else if (lowerType.includes('bangle') || lowerType.includes('bracelet') || lowerType.includes('kada')) {
            return (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <title>Bangle</title>
                    <circle cx="12" cy="12" r="8" />
                    <path d="M12 4v4M12 16v4" />
                </svg>
            );
        } else if (lowerType.includes('earring') || lowerType.includes('tops') || lowerType.includes('jhumka')) {
            return (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <title>Earring</title>
                    <path d="M12 2v20M9 22h6" />
                    <circle cx="12" cy="16" r="3" fill="currentColor" stroke="none" />
                </svg>
            );
        } else if (lowerType.includes('diamond')) {
            return (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <title>Diamond</title>
                    <path d="M6 3h12l4 6-10 12L2 9z" fill="currentColor" fillOpacity="0.2" />
                </svg>
            );
        } else {
            // Default Gem/Jewellery Icon (Better than Star)
            return (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <title>Jewellery</title>
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                </svg>
            );
        }
    };

    return (
        <div className="product-card" onClick={() => onView(item)}>
            <div className="product-image-container">
                {getImageUrl(item.image) ? (
                    <img
                        src={getImageUrl(item.image)}
                        alt={item.name}
                        className="product-img"
                        loading={index < 4 ? 'eager' : 'lazy'}
                        decoding="async"
                        width="400"
                        height="400"
                    />
                ) : (
                    <div className="product-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                    </div>
                )}

                {/* --- CARD INTELLIGENCE --- */}

                {/* 3. Hover Actions (Admin) */}
                {isAdmin && (
                    <div className="admin-card-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="btn-icon-action" title={item.visibility ? "Hide" : "Show"} onClick={(e) => onToggleVisibility(item, e)}>
                            {item.visibility ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" /></svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                            )}
                        </button>
                        <button className="btn-icon-action" title="Edit" onClick={(e) => { e.stopPropagation(); onEdit(item); }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        </button>
                        <button className="btn-icon-action btn-delete-action" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(item._id, e); }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                        </button>
                    </div>
                )}
            </div>

            <div className="product-info">
                <div className="product-title-row">
                    <h3 className="product-title">{item.name}</h3>
                </div>

                <div className="product-meta-row">
                    <span className="meta-type">{getTypeIcon(item.type)} {item.type}</span>
                    <span className="meta-divider">•</span>
                    <span className="meta-purity">{item.purity}</span>
                    <span className="meta-divider">•</span>
                    <span className="meta-weight">{item.weight}g</span>
                </div>

                {item.huid && (
                    <div className="huid-badge" title={`HUID: ${item.huid} — BIS Government Verified`}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L3 7l9 5 9-5-9-5zM3 17l9 5 9-5M3 12l9 5 9-5"/></svg>
                        HUID ✓
                    </div>
                )}

                <div className="product-price">
                    {formatPrice(item.price)}
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
